import React, { useState, useEffect } from 'react';

interface Distributor {
  id: number;
  name: string;
  contact: string;
  phone: string;
  email: string;
  address: string;
  gstNumber: string;
  settledAmount: number;
  unsettledAmount: number;
  overpaidAmount: number;
}

interface DistributorDetailsProps {
  selectedDistributor: Distributor | null;
  onDistributorSelect: (distributor: Distributor | null) => void;
}

// Utility function to get auth token
const getAuthToken = () => {
  const token = localStorage.getItem('authToken');
  console.log('=== Distributor Details Auth Token Debug ===');
  console.log('Token exists:', !!token);
  console.log('Token length:', token ? token.length : 0);
  console.log('Token preview:', token ? token.substring(0, 20) + '...' : 'No token');
  return token;
};

const DistributorDetails: React.FC<DistributorDetailsProps> = ({ 
  selectedDistributor, 
  onDistributorSelect 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddDistributor, setShowAddDistributor] = useState(false);
  const [newDistributor, setNewDistributor] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    gstNumber: '',
    settledAmount: 0,
    unsettledAmount: 0,
    overpaidAmount: 0
  });
  
  const [distributors, setDistributors] = useState<Distributor[]>([]);
  const [isLoadingDistributors, setIsLoadingDistributors] = useState(true);
  const [distributorsError, setDistributorsError] = useState('');
  const [editingDistributor, setEditingDistributor] = useState<Distributor | null>(null);
  const [editForm, setEditForm] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    gstNumber: ''
  });
  const [isUpdatingDistributor, setIsUpdatingDistributor] = useState(false);

  // Fetch distributors data from API
  const fetchDistributorsData = async () => {
    try {
      setIsLoadingDistributors(true);
      setDistributorsError('');
      
      // Get auth token from localStorage
      const authToken = getAuthToken();
      
      if (!authToken) {
        console.warn('⚠️ No auth token found in localStorage. API call may fail.');
        setDistributorsError('Authentication token not found. Please log in again.');
        setIsLoadingDistributors(false);
        return;
      }
      
      console.log('Making API call to distributors endpoint from DistributorDetails...');
      console.log('Using token:', authToken ? 'Yes' : 'No');
      
      const response = await fetch('https://pharma-amd-0-1-42.onrender.com/pharma/api/distributors/', {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
          ...(authToken && { 'Authorization': `Bearer ${authToken}` })
        }
      });
      
      console.log('Distributors API response received:', response.status);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Distributors data received in DistributorDetails:', data);
      console.log('Data type:', typeof data);
      console.log('Is array:', Array.isArray(data));
      console.log('Data length:', data?.length);
      
      // Handle both array and object responses
      let distributorsArray: any[] = [];
      if (Array.isArray(data)) {
        distributorsArray = data;
      } else if (data && Array.isArray(data.distributors)) {
        distributorsArray = data.distributors;
        console.log('Found distributors array in data.distributors:', distributorsArray);
      } else {
        console.warn('API returned unexpected data structure:', data);
        setDistributors([]);
        return;
      }
      
      // Transform distributors to match interface
      const transformedDistributors = distributorsArray.map((dist: any) => ({
        id: dist.id || 0,
        name: dist.name || 'Unknown Distributor',
        contact: dist.contact || dist.email || 'N/A',
        phone: dist.phone || 'N/A',
        email: dist.email || 'N/A',
        address: dist.address || 'N/A',
        gstNumber: dist.gstin || 'N/A',
        settledAmount: dist.settled_amount || 0,
        unsettledAmount: dist.unsettled_amount || 0,
        overpaidAmount: dist.credit_balance || 0
      }));
      
      setDistributors(transformedDistributors);
      console.log('Transformed distributors:', transformedDistributors);
      console.log('Distributors state updated with', transformedDistributors.length, 'items');
    } catch (error) {
      console.error('Error fetching distributors data in DistributorDetails:', error);
      console.error('Error type:', error.constructor.name);
      console.error('Error message:', error.message);
      
      // Check for different types of errors
      if (error.message.includes('CORS') || error.message.includes('Failed to fetch') || 
          error.message.includes('preflight request') || error.message.includes('ERR_FAILED')) {
        setDistributorsError(`
          CORS Error: Unable to connect to the distributors API.
          
          Possible solutions:
          1. Check if your server is running
          2. Verify CORS is properly configured on the server
          3. Ensure ngrok tunnel is active
          4. Try refreshing the page
          
          Server endpoint: https://pharma-amd-0-1-42.onrender.com/pharma/api/distributors
        `);
      } else if (error.message.includes('401') || error.message.includes('Unauthorized')) {
        setDistributorsError('Authentication failed. Please log in again.');
        // Don't auto-remove token - let user manually logout
      } else if (error.message.includes('403') || error.message.includes('Forbidden')) {
        setDistributorsError('Access denied. You do not have permission to view distributors data.');
      } else {
        setDistributorsError(`Failed to load distributors data: ${error.message}`);
      }
      
      // Fallback to sample data if API fails
      console.log('Falling back to sample data due to API error');
      setDistributors([
    { id: 1, name: 'MediCare Distributors', contact: 'John Smith', phone: '+91 98765 43210', email: 'john@medicare.com', address: '123 Medical Plaza, Mumbai, Maharashtra 400001', gstNumber: '27ABCDE1234F1Z5', settledAmount: 125000, unsettledAmount: 45750, overpaidAmount: 8500 },
    { id: 2, name: 'Pharma Solutions Ltd', contact: 'Sarah Johnson', phone: '+91 87654 32109', email: 'sarah@pharmasol.com', address: '456 Health Street, Delhi, Delhi 110001', gstNumber: '07FGHIJ5678K2L6', settledAmount: 89000, unsettledAmount: 32000, overpaidAmount: 0 },
    { id: 3, name: 'HealthCare Supply Co', contact: 'Mike Wilson', phone: '+91 76543 21098', email: 'mike@healthcare.com', address: '789 Pharma Avenue, Bangalore, Karnataka 560001', gstNumber: '29MNOPQ9012R3S7', settledAmount: 156000, unsettledAmount: 78000, overpaidAmount: 12000 },
    { id: 4, name: 'MediCorp Distributors', contact: 'Lisa Brown', phone: '+91 65432 10987', email: 'lisa@medicorp.com', address: '321 Drug Lane, Chennai, Tamil Nadu 600001', gstNumber: '33TUVWX3456Y4Z8', settledAmount: 67000, unsettledAmount: 15000, overpaidAmount: 3500 },
    { id: 5, name: 'PharmaLink Services', contact: 'David Lee', phone: '+91 54321 09876', email: 'david@pharmalink.com', address: '654 Medicine Road, Hyderabad, Telangana 500001', gstNumber: '36ABCDE7890F5G9', settledAmount: 198000, unsettledAmount: 25000, overpaidAmount: 0 }
  ]);
    } finally {
      setIsLoadingDistributors(false);
    }
  };

  // Handle edit distributor
  const handleEditDistributor = (distributor: Distributor) => {
    setEditingDistributor(distributor);
    setEditForm({
      name: distributor.name,
      phone: distributor.phone,
      email: distributor.email,
      address: distributor.address,
      gstNumber: distributor.gstNumber
    });
  };

  // Handle edit form changes
  const handleEditFormChange = (field: string, value: string) => {
    setEditForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Update distributor via API
  const updateDistributor = async () => {
    if (!editingDistributor) return;

    try {
      setIsUpdatingDistributor(true);
      
      const authToken = getAuthToken();
      
      if (!authToken) {
        alert('Authentication token not found. Please log in again.');
        return;
      }

      const updateData = {
        name: editForm.name,
        phone: editForm.phone,
        email: editForm.email,
        address: editForm.address,
        gstin: editForm.gstNumber
      };

      console.log(`Updating distributor ${editingDistributor.id} with data:`, updateData);

      const response = await fetch(`https://pharma-amd-0-1-42.onrender.com/pharma/api/distributors${editingDistributor.id}`, {
        method: 'PUT',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
          ...(authToken && { 'Authorization': `Bearer ${authToken}` })
        },
        body: JSON.stringify(updateData)
      });

      console.log('Update API response received:', response.status);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const updatedData = await response.json();
      console.log('Updated distributor data received:', updatedData);

      // Update the distributors list with the updated data
      setDistributors(prev => prev.map(dist => 
        dist.id === editingDistributor.id 
          ? {
              ...dist,
              name: editForm.name,
              phone: editForm.phone,
              email: editForm.email,
              address: editForm.address,
              gstNumber: editForm.gstNumber
            }
          : dist
      ));

      // Update selected distributor if it's the one being edited
      if (selectedDistributor?.id === editingDistributor.id) {
        onDistributorSelect({
          ...selectedDistributor,
          name: editForm.name,
          phone: editForm.phone,
          email: editForm.email,
          address: editForm.address,
          gstNumber: editForm.gstNumber
        });
      }

      setEditingDistributor(null);
      alert('Distributor updated successfully!');
      
    } catch (error) {
      console.error('Error updating distributor:', error);
      alert(`Failed to update distributor: ${error.message}`);
    } finally {
      setIsUpdatingDistributor(false);
    }
  };

  // Cancel edit
  const cancelEdit = () => {
    setEditingDistributor(null);
    setEditForm({
      name: '',
      phone: '',
      email: '',
      address: '',
      gstNumber: ''
    });
  };

  // Fetch distributors data on component mount
  useEffect(() => {
    fetchDistributorsData();
  }, []);

  const filteredDistributors = distributors.filter(distributor =>
    distributor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    distributor.contact.toLowerCase().includes(searchTerm.toLowerCase()) ||
    distributor.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    distributor.address.toLowerCase().includes(searchTerm.toLowerCase()) ||
    distributor.gstNumber.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  console.log('Distributors state:', distributors);
  console.log('Filtered distributors:', filteredDistributors);
  console.log('Search term:', searchTerm);

  const handleAddDistributor = async () => {
    if (newDistributor.name && newDistributor.phone && newDistributor.email && newDistributor.address && newDistributor.gstNumber) {
      // Check if distributor with same name already exists
      const existingDistributor = distributors.find(d => 
        d.name.toLowerCase() === newDistributor.name.toLowerCase()
      );
      
      if (existingDistributor) {
        alert('A distributor with this name already exists!');
        return;
      }
      
      try {
        // Get auth token from localStorage
        const authToken = getAuthToken();
        
        if (!authToken) {
          alert('Authentication token not found. Please log in again.');
          return;
        }

        // Prepare data for API call
        const distributorData = {
          name: newDistributor.name,
          phone: newDistributor.phone,
          email: newDistributor.email,
          address: newDistributor.address,
          gstin: newDistributor.gstNumber
        };

        console.log('Adding new distributor with data:', distributorData);

        // Make POST API call to add distributor
        const response = await fetch('https://pharma-amd-0-1-42.onrender.com/pharma/api/distributors/', {
          method: 'POST',
          mode: 'cors',
          headers: {
            'Content-Type': 'application/json',
            'ngrok-skip-browser-warning': 'true',
            ...(authToken && { 'Authorization': `Bearer ${authToken}` })
          },
          body: JSON.stringify(distributorData)
        });

        console.log('Add distributor API response received:', response.status);

        if (!response.ok) {
          const errorText = await response.text();
          console.error('Error response:', errorText);
          throw new Error(`HTTP error! status: ${response.status}: ${errorText}`);
        }

        const addedDistributor = await response.json();
        console.log('Added distributor data received:', addedDistributor);

        // Transform the response to match our interface
        const transformedDistributor: Distributor = {
          id: addedDistributor.id || addedDistributor.distributor_id || 0,
          name: addedDistributor.name || newDistributor.name,
          contact: addedDistributor.contact || '',
          phone: addedDistributor.phone || newDistributor.phone,
          email: addedDistributor.email || newDistributor.email,
          address: addedDistributor.address || newDistributor.address,
          gstNumber: addedDistributor.gstin || newDistributor.gstNumber,
          settledAmount: addedDistributor.settled_amount || 0,
          unsettledAmount: addedDistributor.unsettled_amount || 0,
          overpaidAmount: addedDistributor.credit_balance || 0
        };

        // Add the new distributor to the local state
        setDistributors([...distributors, transformedDistributor]);
        
        // Reset form
        setNewDistributor({ name: '', phone: '', email: '', address: '', gstNumber: '', settledAmount: 0, unsettledAmount: 0, overpaidAmount: 0 });
      setShowAddDistributor(false);
        
      alert('Distributor added successfully!');
        
      } catch (error) {
        console.error('Error adding distributor:', error);
        alert(`Failed to add distributor: ${error.message}`);
      }
    } else {
      alert('Please fill in all fields');
    }
  };

  const handleNewDistributorChange = (field: string, value: string) => {
    setNewDistributor(prev => ({
      ...prev,
      [field]: value
    }));
  };

   return (
     <>
      <style>
        {`
          .distributor-container::-webkit-scrollbar {
            display: none;
          }
          .distributor-container {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          
          @keyframes spin {
            0% {
              transform: rotate(0deg);
            }
            100% {
              transform: rotate(360deg);
            }
          }
        `}
      </style>
     <div className="distributor-container" style={{
       margin: '0 2rem 1rem 2rem',
       padding: '2rem',
       background: 'rgba(255, 255, 255, 0.95)',
       borderRadius: '12px',
       border: '1px solid rgba(255, 255, 255, 0.3)',
       boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
       backdropFilter: 'blur(20px)',
       maxHeight: 'calc(100vh - 200px)',
       overflowY: 'auto'
     }}>
      {/* Section Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '1.5rem',
        gap: '1rem'
      }}>
        <h3 style={{
          fontSize: '1.25rem',
          fontWeight: '700',
          color: '#1B5E20',
          margin: 0,
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          flexShrink: 0
        }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 21V19C19 17.9391 18.5786 16.9217 17.8284 16.1716C17.0783 15.4214 16.0609 15 15 15H9C7.93913 15 6.92172 15.4214 6.17157 16.1716C5.42143 16.9217 5 17.9391 5 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Select Distributor
        </h3>
        
        {/* Search Input */}
        <div style={{ 
          flex: 1, 
          maxWidth: '300px', 
          position: 'relative',
          background: 'linear-gradient(135deg, rgba(34, 95, 225, 0.1) 0%, rgba(27, 94, 32, 0.1) 100%)',
          borderRadius: '12px',
          padding: '4px',
          boxShadow: '0 4px 12px rgba(34, 95, 225, 0.2)',
          border: '2px solid rgba(34, 95, 225, 0.3)'
        }}>
          <input
            type="text"
            placeholder="Search distributors..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '0.75rem 1rem 0.75rem 2.5rem',
              border: 'none',
              borderRadius: '8px',
              background: 'rgba(255, 255, 255, 0.95)',
              fontSize: '0.9rem',
              outline: 'none',
              color: '#374151',
              fontWeight: '500',
              transition: 'all 0.3s ease',
              boxSizing: 'border-box',
              boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.05)'
            }}
            onFocus={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 1)';
              e.currentTarget.style.boxShadow = 'inset 0 2px 4px rgba(0, 0, 0, 0.05), 0 0 0 3px rgba(34, 95, 225, 0.2)';
            }}
            onBlur={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.95)';
              e.currentTarget.style.boxShadow = 'inset 0 2px 4px rgba(0, 0, 0, 0.05)';
            }}
          />
          <svg 
            width="16" 
            height="16" 
            viewBox="0 0 24 24" 
            fill="none" 
            xmlns="http://www.w3.org/2000/svg"
            style={{
              position: 'absolute',
              left: '1.25rem',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#225fe1',
              pointerEvents: 'none',
              filter: 'drop-shadow(0 1px 2px rgba(34, 95, 225, 0.3))'
            }}
          >
            <path d="M21 21L16.514 16.506L21 21ZM19 10.5C19 15.194 15.194 19 10.5 19C5.806 19 2 15.194 2 10.5C2 5.806 5.806 2 10.5 2C15.194 2 19 5.806 19 10.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        
        <button
          onClick={() => setShowAddDistributor(!showAddDistributor)}
          style={{
            background: 'linear-gradient(135deg, #2E7D32 0%, #1B5E20 50%, #0D4A14 100%)',
            color: 'white',
            border: 'none',
            padding: '0.75rem 1.5rem',
            borderRadius: '8px',
            fontSize: '0.9rem',
            fontWeight: '600',
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(46, 125, 50, 0.3)',
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            flexShrink: 0
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-1px)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(46, 125, 50, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(46, 125, 50, 0.3)';
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Add Distributor
        </button>
      </div>

      {/* Add Distributor Form */}
      {showAddDistributor && (
        <div style={{
          marginBottom: '1.5rem',
          padding: '1.5rem',
          background: 'rgba(248, 250, 252, 0.8)',
          borderRadius: '8px',
          border: '1px solid rgba(226, 232, 240, 0.5)'
        }}>
          <h4 style={{
            fontSize: '1rem',
            fontWeight: '600',
            color: '#1B5E20',
            margin: '0 0 1rem 0'
          }}>
            Add New Distributor
          </h4>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '1rem'
          }}>
            <div>
              <label style={{
                display: 'block',
                fontSize: '0.85rem',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '0.5rem'
              }}>
                Company Name *
              </label>
              <input
                type="text"
                placeholder="Enter company name"
                value={newDistributor.name}
                onChange={(e) => handleNewDistributorChange('name', e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '2px solid rgba(226, 232, 240, 0.8)',
                  borderRadius: '8px',
                  background: 'rgba(255, 255, 255, 0.9)',
                  fontSize: '0.9rem',
                  outline: 'none',
                  color: '#374151',
                  fontWeight: '500',
                  transition: 'all 0.3s ease',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#225fe1';
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 1)';
                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(34, 95, 225, 0.1)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(226, 232, 240, 0.8)';
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              />
            </div>
            <div>
              <label style={{
                display: 'block',
                fontSize: '0.85rem',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '0.5rem'
              }}>
                Phone Number *
              </label>
              <input
                type="tel"
                placeholder="Enter phone number"
                value={newDistributor.phone}
                onChange={(e) => handleNewDistributorChange('phone', e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '2px solid rgba(226, 232, 240, 0.8)',
                  borderRadius: '8px',
                  background: 'rgba(255, 255, 255, 0.9)',
                  fontSize: '0.9rem',
                  outline: 'none',
                  color: '#374151',
                  fontWeight: '500',
                  transition: 'all 0.3s ease',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#225fe1';
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 1)';
                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(34, 95, 225, 0.1)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(226, 232, 240, 0.8)';
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              />
            </div>
            <div>
              <label style={{
                display: 'block',
                fontSize: '0.85rem',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '0.5rem'
              }}>
                Email Address *
              </label>
              <input
                type="email"
                placeholder="Enter email address"
                value={newDistributor.email}
                onChange={(e) => handleNewDistributorChange('email', e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '2px solid rgba(226, 232, 240, 0.8)',
                  borderRadius: '8px',
                  background: 'rgba(255, 255, 255, 0.9)',
                  fontSize: '0.9rem',
                  outline: 'none',
                  color: '#374151',
                  fontWeight: '500',
                  transition: 'all 0.3s ease',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#225fe1';
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 1)';
                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(34, 95, 225, 0.1)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(226, 232, 240, 0.8)';
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              />
            </div>
            <div>
              <label style={{
                display: 'block',
                fontSize: '0.85rem',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '0.5rem'
              }}>
                Address *
              </label>
              <textarea
                placeholder="Enter complete address"
                value={newDistributor.address}
                onChange={(e) => handleNewDistributorChange('address', e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '2px solid rgba(226, 232, 240, 0.8)',
                  borderRadius: '8px',
                  background: 'rgba(255, 255, 255, 0.9)',
                  fontSize: '0.9rem',
                  outline: 'none',
                  color: '#374151',
                  fontWeight: '500',
                  transition: 'all 0.3s ease',
                  boxSizing: 'border-box',
                  minHeight: '80px',
                  resize: 'vertical'
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#225fe1';
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 1)';
                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(34, 95, 225, 0.1)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(226, 232, 240, 0.8)';
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              />
            </div>
            <div>
              <label style={{
                display: 'block',
                fontSize: '0.85rem',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '0.5rem'
              }}>
                GST Number *
              </label>
              <input
                type="text"
                placeholder="Enter GST number (e.g., 27ABCDE1234F1Z5)"
                value={newDistributor.gstNumber}
                onChange={(e) => handleNewDistributorChange('gstNumber', e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '2px solid rgba(226, 232, 240, 0.8)',
                  borderRadius: '8px',
                  background: 'rgba(255, 255, 255, 0.9)',
                  fontSize: '0.9rem',
                  outline: 'none',
                  color: '#374151',
                  fontWeight: '500',
                  transition: 'all 0.3s ease',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#225fe1';
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 1)';
                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(34, 95, 225, 0.1)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(226, 232, 240, 0.8)';
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              />
            </div>
          </div>
          <div style={{
            display: 'flex',
            gap: '1rem',
            marginTop: '1rem',
            justifyContent: 'flex-end'
          }}>
            <button
              onClick={() => {
                setShowAddDistributor(false);
                setNewDistributor({ name: '', phone: '', email: '', address: '', gstNumber: '', settledAmount: 0, unsettledAmount: 0, overpaidAmount: 0 });
              }}
              style={{
                background: 'rgba(107, 114, 128, 0.1)',
                color: '#6b7280',
                border: '2px solid rgba(107, 114, 128, 0.3)',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                fontSize: '0.9rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(107, 114, 128, 0.2)';
                e.currentTarget.style.borderColor = 'rgba(107, 114, 128, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(107, 114, 128, 0.1)';
                e.currentTarget.style.borderColor = 'rgba(107, 114, 128, 0.3)';
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleAddDistributor}
              style={{
                background: 'linear-gradient(135deg, #2E7D32 0%, #1B5E20 50%, #0D4A14 100%)',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                fontSize: '0.9rem',
                fontWeight: '600',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(46, 125, 50, 0.3)',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(46, 125, 50, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(46, 125, 50, 0.3)';
              }}
            >
              Add Distributor
            </button>
          </div>
        </div>
      )}

      {/* Edit Distributor Form */}
      {editingDistributor && (
        <div style={{
          marginBottom: '1.5rem',
          padding: '1.5rem',
          background: 'rgba(59, 130, 246, 0.05)',
          borderRadius: '8px',
          border: '1px solid rgba(59, 130, 246, 0.2)'
        }}>
          <h4 style={{
            fontSize: '1rem',
            fontWeight: '600',
            color: '#1B5E20',
            margin: '0 0 1rem 0',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
            Edit Distributor: {editingDistributor.name}
          </h4>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '1rem'
          }}>
            <div>
              <label style={{
                display: 'block',
                fontSize: '0.85rem',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '0.5rem'
              }}>
                Company Name *
              </label>
              <input
                type="text"
                placeholder="Enter company name"
                value={editForm.name}
                onChange={(e) => handleEditFormChange('name', e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '2px solid rgba(59, 130, 246, 0.3)',
                  borderRadius: '8px',
                  background: 'rgba(255, 255, 255, 0.9)',
                  fontSize: '0.9rem',
                  outline: 'none',
                  color: '#374151',
                  fontWeight: '500',
                  transition: 'all 0.3s ease',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#3b82f6';
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 1)';
                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.3)';
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              />
            </div>
            <div>
              <label style={{
                display: 'block',
                fontSize: '0.85rem',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '0.5rem'
              }}>
                Phone Number *
              </label>
              <input
                type="tel"
                placeholder="Enter phone number"
                value={editForm.phone}
                onChange={(e) => handleEditFormChange('phone', e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '2px solid rgba(59, 130, 246, 0.3)',
                  borderRadius: '8px',
                  background: 'rgba(255, 255, 255, 0.9)',
                  fontSize: '0.9rem',
                  outline: 'none',
                  color: '#374151',
                  fontWeight: '500',
                  transition: 'all 0.3s ease',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#3b82f6';
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 1)';
                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.3)';
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              />
            </div>
            <div>
              <label style={{
                display: 'block',
                fontSize: '0.85rem',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '0.5rem'
              }}>
                Email Address *
              </label>
              <input
                type="email"
                placeholder="Enter email address"
                value={editForm.email}
                onChange={(e) => handleEditFormChange('email', e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '2px solid rgba(59, 130, 246, 0.3)',
                  borderRadius: '8px',
                  background: 'rgba(255, 255, 255, 0.9)',
                  fontSize: '0.9rem',
                  outline: 'none',
                  color: '#374151',
                  fontWeight: '500',
                  transition: 'all 0.3s ease',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#3b82f6';
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 1)';
                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.3)';
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              />
            </div>
            <div>
              <label style={{
                display: 'block',
                fontSize: '0.85rem',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '0.5rem'
              }}>
                Address *
              </label>
              <textarea
                placeholder="Enter complete address"
                value={editForm.address}
                onChange={(e) => handleEditFormChange('address', e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '2px solid rgba(59, 130, 246, 0.3)',
                  borderRadius: '8px',
                  background: 'rgba(255, 255, 255, 0.9)',
                  fontSize: '0.9rem',
                  outline: 'none',
                  color: '#374151',
                  fontWeight: '500',
                  transition: 'all 0.3s ease',
                  boxSizing: 'border-box',
                  minHeight: '80px',
                  resize: 'vertical'
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#3b82f6';
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 1)';
                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.3)';
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              />
            </div>
            <div>
              <label style={{
                display: 'block',
                fontSize: '0.85rem',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '0.5rem'
              }}>
                GST Number *
              </label>
              <input
                type="text"
                placeholder="Enter GST number (e.g., 27ABCDE1234F1Z5)"
                value={editForm.gstNumber}
                onChange={(e) => handleEditFormChange('gstNumber', e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '2px solid rgba(59, 130, 246, 0.3)',
                  borderRadius: '8px',
                  background: 'rgba(255, 255, 255, 0.9)',
                  fontSize: '0.9rem',
                  outline: 'none',
                  color: '#374151',
                  fontWeight: '500',
                  transition: 'all 0.3s ease',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#3b82f6';
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 1)';
                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.3)';
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              />
            </div>
          </div>
          <div style={{
            display: 'flex',
            gap: '1rem',
            marginTop: '1rem',
            justifyContent: 'flex-end'
          }}>
            <button
              onClick={cancelEdit}
              disabled={isUpdatingDistributor}
              style={{
                background: 'rgba(107, 114, 128, 0.1)',
                color: '#6b7280',
                border: '2px solid rgba(107, 114, 128, 0.3)',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                fontSize: '0.9rem',
                fontWeight: '600',
                cursor: isUpdatingDistributor ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                opacity: isUpdatingDistributor ? 0.6 : 1
              }}
              onMouseEnter={(e) => {
                if (!isUpdatingDistributor) {
                  e.currentTarget.style.background = 'rgba(107, 114, 128, 0.2)';
                  e.currentTarget.style.borderColor = 'rgba(107, 114, 128, 0.5)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isUpdatingDistributor) {
                  e.currentTarget.style.background = 'rgba(107, 114, 128, 0.1)';
                  e.currentTarget.style.borderColor = 'rgba(107, 114, 128, 0.3)';
                }
              }}
            >
              Cancel
            </button>
            <button
              onClick={updateDistributor}
              disabled={isUpdatingDistributor}
              style={{
                background: isUpdatingDistributor 
                  ? 'rgba(59, 130, 246, 0.5)' 
                  : 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                fontSize: '0.9rem',
                fontWeight: '600',
                cursor: isUpdatingDistributor ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                boxShadow: isUpdatingDistributor ? 'none' : '0 2px 8px rgba(59, 130, 246, 0.3)'
              }}
              onMouseEnter={(e) => {
                if (!isUpdatingDistributor) {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.4)';
                  e.currentTarget.style.background = 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isUpdatingDistributor) {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(59, 130, 246, 0.3)';
                  e.currentTarget.style.background = 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)';
                }
              }}
            >
              {isUpdatingDistributor ? (
                <>
                  <div style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                    borderTop: '2px solid white',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                  Updating...
                </>
              ) : (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 6L9 17l-5-5"/>
                  </svg>
                  Update Distributor
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Selected Distributor Display */}
      <div style={{
        marginBottom: '1rem',
        padding: '0.75rem 1rem',
        background: selectedDistributor ? 'rgba(34, 197, 94, 0.1)' : 'rgba(34, 95, 225, 0.1)',
        border: `2px solid ${selectedDistributor ? 'rgba(34, 197, 94, 0.3)' : 'rgba(34, 95, 225, 0.3)'}`,
        borderRadius: '8px',
        fontSize: '0.9rem',
        fontWeight: '600',
        color: selectedDistributor ? '#22c55e' : '#225fe1',
        textAlign: 'center'
      }}>
        {selectedDistributor ? selectedDistributor.name : 'No distributor selected'}
      </div>

      {/* Loading State */}
      {isLoadingDistributors && (
        <div style={{
          textAlign: 'center',
          padding: '2rem',
          color: '#6b7280'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #e5e7eb',
            borderTop: '4px solid #22c55e',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 1rem auto'
          }}></div>
          <h3 style={{
            fontSize: '1rem',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '0.5rem'
          }}>
            Loading Distributors...
          </h3>
          <p style={{
            fontSize: '0.9rem',
            color: '#6b7280'
          }}>
            Please wait while we fetch the distributor data
          </p>
        </div>
      )}

      {/* Error State */}
      {distributorsError && !isLoadingDistributors && (
        <div style={{
          textAlign: 'center',
          padding: '2rem',
          color: '#6b7280'
        }}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" style={{ marginBottom: '1rem', opacity: 0.5 }}>
            <circle cx="12" cy="12" r="10"/>
            <line x1="15" y1="9" x2="9" y2="15"/>
            <line x1="9" y1="9" x2="15" y2="15"/>
          </svg>
          <h3 style={{
            fontSize: '1rem',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '0.5rem'
          }}>
            Error Loading Distributors
          </h3>
          <p style={{
            fontSize: '0.85rem',
            color: '#6b7280',
            marginBottom: '1rem',
            whiteSpace: 'pre-line'
          }}>
            {distributorsError}
          </p>
          <button
            onClick={fetchDistributorsData}
            style={{
              background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
              color: 'white',
              border: 'none',
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              fontSize: '0.9rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 8px 20px rgba(34, 197, 94, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            Try Again
          </button>
        </div>
      )}

       {/* Distributor List */}
       {!isLoadingDistributors && !distributorsError && (
       <div style={{
         display: 'grid',
         gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
         gap: '1rem',
         padding: '0.5rem',
         background: 'rgba(248, 250, 252, 0.5)',
         borderRadius: '8px',
         border: '1px solid rgba(226, 232, 240, 0.3)'
       }}>
        {/* Filtered Distributors */}
        {filteredDistributors.length > 0 ? filteredDistributors.map((distributor) => (
          <div
            key={distributor.id}
            style={{
              padding: '1rem',
              background: selectedDistributor?.id === distributor.id ? 'rgba(34, 197, 94, 0.1)' : 'rgba(255, 255, 255, 0.9)',
              border: `2px solid ${selectedDistributor?.id === distributor.id ? 'rgba(34, 197, 94, 0.5)' : 'rgba(226, 232, 240, 0.5)'}`,
              borderRadius: '8px',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: selectedDistributor?.id === distributor.id ? '0 4px 12px rgba(34, 197, 94, 0.2)' : '0 2px 4px rgba(0, 0, 0, 0.05)'
            }}
            onMouseEnter={(e) => {
              if (selectedDistributor?.id !== distributor.id) {
                e.currentTarget.style.borderColor = '#225fe1';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 1)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(34, 95, 225, 0.15)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }
            }}
            onMouseLeave={(e) => {
              if (selectedDistributor?.id !== distributor.id) {
                e.currentTarget.style.borderColor = 'rgba(226, 232, 240, 0.5)';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)';
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)';
                e.currentTarget.style.transform = 'translateY(0)';
              }
            }}
            onClick={() => {
              onDistributorSelect(distributor);
            }}
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '0.5rem'
            }}>
              <h4 style={{
                fontSize: '1rem',
                fontWeight: '600',
                color: '#1B5E20',
                margin: 0
              }}>
                {distributor.name}
              </h4>
              <div style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                background: selectedDistributor?.id === distributor.id ? 'rgba(34, 197, 94, 0.8)' : 'rgba(34, 197, 94, 0.2)',
                border: `2px solid ${selectedDistributor?.id === distributor.id ? 'rgba(34, 197, 94, 1)' : 'rgba(34, 197, 94, 0.5)'}`
              }}></div>
            </div>
            <div style={{
              fontSize: '0.85rem',
              color: '#64748b',
              marginBottom: '0.25rem'
            }}>
              <strong>Contact:</strong> {distributor.contact}
            </div>
            <div style={{
              fontSize: '0.85rem',
              color: '#64748b',
              marginBottom: '0.25rem'
            }}>
              <strong>Phone:</strong> {distributor.phone}
            </div>
            <div style={{
              fontSize: '0.85rem',
              color: '#64748b',
              marginBottom: '0.25rem'
            }}>
              <strong>Email:</strong> {distributor.email}
            </div>
            <div style={{
              fontSize: '0.85rem',
              color: '#64748b',
              marginBottom: '0.25rem'
            }}>
              <strong>Address:</strong> {distributor.address}
            </div>
            <div style={{
              fontSize: '0.85rem',
              color: '#64748b',
              marginBottom: selectedDistributor?.id === distributor.id ? '1rem' : '0'
            }}>
              <strong>GST:</strong> {distributor.gstNumber}
            </div>

             {/* Action Buttons - Only show for selected distributor */}
             {selectedDistributor?.id === distributor.id && (
               <div style={{
                 marginTop: '1rem',
                   display: 'flex',
                   gap: '0.75rem',
                 justifyContent: 'center'
               }}>
                 <button
                   onClick={() => handleEditDistributor(distributor)}
                   style={{
                     background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                     color: 'white',
                     border: 'none',
                     padding: '0.75rem 1.5rem',
                     borderRadius: '8px',
                     fontSize: '0.85rem',
                       fontWeight: '600',
                     cursor: 'pointer',
                     transition: 'all 0.3s ease',
                     display: 'flex',
                     alignItems: 'center',
                     gap: '0.5rem',
                     boxShadow: '0 2px 8px rgba(59, 130, 246, 0.2)'
                   }}
                   onMouseEnter={(e) => {
                     e.currentTarget.style.transform = 'translateY(-2px)';
                     e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
                     e.currentTarget.style.background = 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)';
                   }}
                   onMouseLeave={(e) => {
                     e.currentTarget.style.transform = 'translateY(0)';
                     e.currentTarget.style.boxShadow = '0 2px 8px rgba(59, 130, 246, 0.2)';
                     e.currentTarget.style.background = 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)';
                   }}
                 >
                   <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                     <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                     <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                     </svg>
                   Edit Details
                 </button>
               </div>
             )}
          </div>
        )) : (
          <div style={{
            gridColumn: '1 / -1',
            textAlign: 'center',
            padding: '2rem',
            color: '#64748b',
            fontSize: '0.9rem'
          }}>
            No distributors found matching "{searchTerm}"
          </div>
        )}
       </div>
       )}
     </div>
     </>
   );
 };

export default DistributorDetails;
